import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'logic/moment';
import { FormattedMessage } from 'react-intl';

import { orange } from '@material-ui/core/colors';

import {
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  withStyles,
  withMobileDialog,
  Typography,
} from '@material-ui/core';

import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';
import EditIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import SubdirectoryArrowRight from '@material-ui/icons/SubdirectoryArrowRight';
import ConfirmationButton from 'components/shared/ConfirmationButton';
import PATHS from 'constants/paths';

const rating = [1, 2, 3, 4, 5];
const fontSize = 16;

const styles = (theme) => ({
  paper: {
    margin: theme.spacing(2),
  },
  listItem: {
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  rating: {
    textAlign: 'right',

    [theme.breakpoints.down('xs')]: {
      flex: 1,
      textAlign: 'center',
    },
  },
  badgeContainer: {
    display: 'inline',
    marginLeft: '10px',

    [theme.breakpoints.down('xs')]: {
      display: 'block',
      marginLeft: '-3px',
    },
  },
  badge: {
    backgroundColor: theme.palette.grey[500],
    borderRadius: 4,
    color: theme.palette.common.white,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    padding: `0 ${theme.spacing(1)}px`,
  },
  orange: {
    backgroundColor: orange[500],
  },
  star: {
    verticalAlign: 'middle',
  },
});

const waitingApproval = (
  <FormattedMessage
    id="reviews.waitingApproval"
    defaultMessage="Waiting for approval"
  />
);

const Stars = ({ score, classes }) => {
  return (
    <div>
      {
        rating.map((rate) => {
          if (score >= rate) {
            return (
              <Star
                key={rate}
                style={{ fontSize }}
                color="primary"
                className={classes.star}
              />
            );
          }

          return (
            <StarBorder
              key={rate}
              style={{ fontSize }}
              color="primary"
              className={classes.star}
            />
          );
        })
      }
    </div>
  );
};

Stars.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  classes: PropTypes.shape({}),
};

const ReviewsList = ({
  classes,
  userId,
  setReplyFormId,
  review,
  deleteReview,
  deleteReviewReply,
  setEditingReply,
}) => {
  const {
    body,
    created_at,
    id,
    reply,
    reviewed_at,
    reviewed_user_id,
    score,
    user,
    ...other
  } = review;

  return (
    <Paper
      className={classes.paper}
    >
      <List>
        <ListItem>
          <ListItemText
            secondary={`#${other.cottage.id} - ${other.cottage.title_sv}`}
          />
          {
            reviewed_user_id !== userId && (
              <>
                <IconButton
                  color="primary"
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={PATHS.reviews.edit.replace(':id', id)}
                >
                  <EditIcon />
                </IconButton>

                <ConfirmationButton
                  color="secondary"
                  dialogText={(
                    <FormattedMessage
                      id="review.deleteConfirmation"
                      defaultMessage="Are you sure you want to delete your review? This cannot be undone."
                    />
                  )}
                  onConfirm={() => deleteReview(id)}
                >
                  <DeleteIcon />
                </ConfirmationButton>
              </>

            )
          }
        </ListItem>
        <ListItem divider className={classes.listItem}>
          <ListItemText
            primary={body}
            secondary={(
              <>
                <span>
                  {moment(created_at).format('LL')} -
                  <span className={classes.badgeContainer}>
                    {reviewed_user_id !== userId ? (
                      <span className={classes.badge}>
                        <FormattedMessage
                          id="You"
                          defaultMessage="You"
                        />
                      </span>
                    ) : (
                      <FormattedMessage
                        id={user.name}
                        defaultMessage={user.name}
                      />
                    )}
                    {!reviewed_at && (
                      <span className={classNames(classes.badge, classes.orange)}>
                        {waitingApproval}
                      </span>
                    )}
                  </span>
                </span>
              </>
            )}
          />
          <div className={classes.rating}>
            <Typography variant="body2" component="div">
              <FormattedMessage
                id="reviews.score"
                defaultMessage="How was your overall experience?"
              />
              &nbsp;
              <Stars score={score} classes={classes} />
            </Typography>
            {
              !!other.cleanliness && (
                <Typography variant="body2" component="div">
                  <FormattedMessage
                    id="reviews.cleanliness"
                    defaultMessage="How good was the cleaning condition?"
                  />
                  &nbsp;
                  <Stars score={other.cleanliness} classes={classes} />
                </Typography>
              )
            }
            {
              !!other.location && (
                <Typography variant="body2" component="div">
                  <FormattedMessage
                    id="reviews.location"
                    defaultMessage="How good is the location of this cottage?"
                  />
                  &nbsp;
                  <Stars score={other.location} classes={classes} />
                </Typography>
              )
            }
            {
              !!other.accuracy && (
                <Typography variant="body2" component="div">
                  <FormattedMessage
                    id="reviews.accuracy"
                    defaultMessage="How much did the ad match reality?"
                  />
                  &nbsp;
                  <Stars score={other.accuracy} classes={classes} />
                </Typography>
              )
            }
          </div>
        </ListItem>
        <ListItem>
          <SubdirectoryArrowRight
            style={{ fontSize }}
            color="disabled"
          />
          {reply && (
            <>
              <ListItemText
                primary={reply.body}
                secondary={(
                  <>
                    {moment(created_at).format('LL')} -{' '}
                    <span className={classes.badgeContainer}>
                      {reply.user.id === userId ? (
                        <span className={classes.badge}>
                          <FormattedMessage
                            id="You"
                            defaultMessage="You"
                          />
                        </span>
                      ) : (
                        <FormattedMessage
                          id={reply.user.name}
                          defaultMessage={reply.user.name}
                        />
                      )}
                      {!reply.reviewed_at && (
                        <span className={classNames(classes.badge, classes.orange)}>
                          {waitingApproval}
                        </span>
                      )}
                    </span>
                  </>
                )}
              />
              {
                reply.user.id === userId && (
                  <>
                    <IconButton
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={() => setEditingReply(reply.id)}
                    >
                      <EditIcon />
                    </IconButton>

                    <ConfirmationButton
                      color="secondary"
                      dialogText={(
                        <FormattedMessage
                          id="reply.deleteConfirmation"
                          defaultMessage="Are you sure you want to delete your reply? This cannot be undone."
                        />
                      )}
                      onConfirm={() => deleteReviewReply(reply.id)}
                    >
                      <DeleteIcon />
                    </ConfirmationButton>
                  </>

                )
              }
            </>
          )}
          {!reply && (
            <ListItemText
              secondary={(
                <FormattedMessage
                  id="reviews.noReply"
                  defaultMessage="No reply yet"
                />
              )}
            />
          )}
          {!reply && reviewed_user_id === userId && (
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={() => setReplyFormId({ id })}
            >
              <FormattedMessage
                id="Reply"
                defaultMessage="Reply"
              />
            </Button>
          )}
        </ListItem>
      </List>
    </Paper>
  );
};

ReviewsList.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  review: PropTypes.shape({
    body: PropTypes.string,
    created_at: PropTypes.string,
    id: PropTypes.number,
    reply: PropTypes.shape({
      id: PropTypes.number,
      reviewed_at: PropTypes.string,
      body: PropTypes.string,
      user: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    }),
    reviewed_at: PropTypes.string,
    reviewed_user_id: PropTypes.number,
    score: PropTypes.number,
    user: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    cottage: PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
    }),
    cleanliness: PropTypes.number,
    location: PropTypes.number,
    accuracy: PropTypes.number,
  }).isRequired,
  userId: PropTypes.number,
  setReplyFormId: PropTypes.func.isRequired,
  setEditingReply: PropTypes.func.isRequired,
  deleteReview: PropTypes.func.isRequired,
  deleteReviewReply: PropTypes.func.isRequired,
};

export default withStyles(styles)(withMobileDialog()(ReviewsList));
